import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Dimensions, FlatList, Image, Modal, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { BlurView } from '@react-native-community/blur';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Radius, Shadow, Spacing, Typography } from '../../theme';
import { getRecentGifs, getTrendingGifs, saveRecentGif, searchGifs, type ChatGif } from '../../services/ChatGifService';
import { useI18n } from '../../i18n';

const { height, width } = Dimensions.get('window');
const GRID_GAP = 8;
const COLUMN_COUNT = 2;
const ITEM_WIDTH = Math.floor((width - (Spacing.base * 2) - GRID_GAP) / COLUMN_COUNT);

interface Props {
  visible: boolean;
  onClose: () => void;
  onSelect: (gif: ChatGif) => void;
}

const PlaceholderGrid = () => (
  <View style={styles.placeholderGrid}>
    {Array.from({ length: 8 }, (_, index) => (
      <View key={index} style={styles.placeholderTile}>
        <LinearGradient
          colors={['rgba(255,255,255,0.08)', 'rgba(255,255,255,0.035)', 'rgba(255,255,255,0.07)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
      </View>
    ))}
  </View>
);

const GifPickerBottomSheet: React.FC<Props> = ({ visible, onClose, onSelect }) => {
  const { t } = useI18n();
  const slide = useRef(new Animated.Value(0)).current;
  const [query, setQuery] = useState('');
  const [gifs, setGifs] = useState<ChatGif[]>([]);
  const [recent, setRecent] = useState<ChatGif[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (nextQuery: string) => {
    setLoading(true);
    setError('');
    try {
      const next = nextQuery.trim() ? await searchGifs(nextQuery) : await getTrendingGifs();
      setGifs(next);
    } catch (err) {
      console.warn('[Tonyte][RN][GIF] provider load failed', err);
      setError(t('chat.gifsLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!visible) return;
    setQuery('');
    getRecentGifs().then(setRecent).catch(() => undefined);
    void load('');
    slide.setValue(0);
    Animated.spring(slide, { toValue: 1, useNativeDriver: true, damping: 24, stiffness: 210 }).start();
  }, [load, slide, visible]);

  useEffect(() => {
    if (!visible) return undefined;
    const timer = setTimeout(() => void load(query), query.trim() ? 260 : 80);
    return () => clearTimeout(timer);
  }, [load, query, visible]);

  const sheetStyle = useMemo(() => ({
    transform: [{ translateY: slide.interpolate({ inputRange: [0, 1], outputRange: [height * 0.82, 0] }) }],
    opacity: slide,
  }), [slide]);

  const handleSelect = useCallback(async (gif: ChatGif) => {
    await saveRecentGif(gif);
    onSelect(gif);
    onClose();
  }, [onClose, onSelect]);

  const renderGif = useCallback(({ item }: { item: ChatGif }) => {
    const aspectRatio = Math.max(0.7, Math.min(1.55, item.width / Math.max(1, item.height)));
    return (
      <TouchableOpacity style={[styles.gifTile, { width: ITEM_WIDTH, aspectRatio }]} activeOpacity={0.86} onPress={() => void handleSelect(item)}>
        <Image source={{ uri: item.previewUrl }} style={StyleSheet.absoluteFill} resizeMode="cover" />
        <LinearGradient
          pointerEvents="none"
          colors={['transparent', 'rgba(0,0,0,0.26)']}
          style={StyleSheet.absoluteFill}
        />
      </TouchableOpacity>
    );
  }, [handleSelect]);

  if (!visible) return null;

  return (
    <Modal visible transparent animationType="none" statusBarTranslucent onRequestClose={onClose}>
      <View style={StyleSheet.absoluteFill}>
        <BlurView
          pointerEvents="none"
          blurType="dark"
          blurAmount={14}
          reducedTransparencyFallbackColor="rgba(0,0,0,0.66)"
          style={styles.backdropBlur}
        />
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={onClose} />
        <Animated.View style={[styles.sheet, sheetStyle]}>
          <LinearGradient
            pointerEvents="none"
            colors={['rgba(255,255,255,0.11)', 'rgba(255,255,255,0.04)', 'rgba(242,125,38,0.025)']}
            locations={[0, 0.6, 1]}
            style={StyleSheet.absoluteFill}
          />
          <View style={styles.grabber} />
          <View style={styles.headerRow}>
            <View>
              <Text style={styles.label}>{t('chat.gifs')}</Text>
              <Text style={styles.title}>{query.trim() ? t('chat.gifSearchResults') : t('chat.gifTrendingNow')}</Text>
            </View>
            <TouchableOpacity style={styles.closeBtn} onPress={onClose} activeOpacity={0.82}>
              <Icon name="x" size={18} color={Colors.textPrimary} />
            </TouchableOpacity>
          </View>

          <View style={styles.searchBox}>
            <Icon name="search" size={15} color="rgba(255,255,255,0.45)" />
            <TextInput
              value={query}
              onChangeText={setQuery}
              placeholder={t('chat.searchGiphy')}
              placeholderTextColor="rgba(255,255,255,0.35)"
              style={styles.searchInput}
              autoCorrect={false}
              autoCapitalize="none"
            />
            {query ? (
              <TouchableOpacity onPress={() => setQuery('')} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Icon name="x-circle" size={15} color="rgba(255,255,255,0.42)" />
              </TouchableOpacity>
            ) : null}
          </View>

          {!query.trim() && recent.length > 0 ? (
            <View style={styles.recentSection}>
              <Text style={styles.sectionTitle}>{t('chat.recentlyUsed')}</Text>
              <FlatList
                horizontal
                data={recent}
                keyExtractor={item => `recent-${item.id}`}
                renderItem={({ item }) => (
                  <TouchableOpacity style={styles.recentTile} activeOpacity={0.86} onPress={() => void handleSelect(item)}>
                    <Image source={{ uri: item.previewUrl }} style={StyleSheet.absoluteFill} resizeMode="cover" />
                  </TouchableOpacity>
                )}
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.recentList}
              />
            </View>
          ) : null}

          {loading && gifs.length === 0 ? (
            <PlaceholderGrid />
          ) : error ? (
            <View style={styles.errorState}>
              <Icon name="wifi-off" size={24} color="rgba(255,255,255,0.42)" />
              <Text style={styles.errorText}>{error}</Text>
              <TouchableOpacity style={styles.retryBtn} onPress={() => void load(query)} activeOpacity={0.84}>
                <Text style={styles.retryText}>{t('common.retry')}</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <FlatList
              data={gifs}
              keyExtractor={item => item.id}
              renderItem={renderGif}
              numColumns={COLUMN_COUNT}
              columnWrapperStyle={styles.gridRow}
              showsVerticalScrollIndicator={false}
              initialNumToRender={8}
              maxToRenderPerBatch={8}
              windowSize={7}
              removeClippedSubviews
              contentContainerStyle={styles.grid}
            />
          )}
        </Animated.View>
      </View>
    </Modal>
  );
};

export default GifPickerBottomSheet;

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(0,0,0,0.46)',
  },
  backdropBlur: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  sheet: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: height * 0.78,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    backgroundColor: 'rgba(7,11,20,0.96)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    overflow: 'hidden',
    paddingHorizontal: Spacing.base,
    paddingTop: 10,
    ...Shadow.strong,
  },
  grabber: {
    alignSelf: 'center',
    width: 42,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.22)',
    marginBottom: 14,
  },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: Spacing.md },
  label: { color: Colors.sunset, fontSize: 10, fontWeight: Typography.extrabold, letterSpacing: 1 },
  title: { color: Colors.textPrimary, fontSize: Typography.lg, fontWeight: Typography.extrabold, marginTop: 2 },
  closeBtn: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.13)',
  },
  searchBox: {
    height: 44,
    borderRadius: 22,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingHorizontal: 14,
    backgroundColor: 'rgba(255,255,255,0.075)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.13)',
  },
  searchInput: { flex: 1, color: Colors.textPrimary, fontSize: Typography.sm, padding: 0 },
  recentSection: { marginTop: Spacing.md },
  sectionTitle: { color: 'rgba(255,255,255,0.62)', fontSize: Typography.xs, fontWeight: Typography.bold, marginBottom: Spacing.sm },
  recentList: { gap: 8, paddingRight: Spacing.base },
  recentTile: { width: 92, height: 70, borderRadius: Radius.md, overflow: 'hidden', backgroundColor: Colors.bgCardAlt },
  grid: { paddingTop: Spacing.md, paddingBottom: 36, gap: GRID_GAP },
  gridRow: { gap: GRID_GAP, marginBottom: GRID_GAP },
  gifTile: {
    borderRadius: Radius.lg,
    overflow: 'hidden',
    backgroundColor: Colors.bgCardAlt,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  placeholderGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: GRID_GAP, paddingTop: Spacing.md },
  placeholderTile: {
    width: ITEM_WIDTH,
    height: 150,
    borderRadius: Radius.lg,
    overflow: 'hidden',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  errorState: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: Spacing.md, paddingHorizontal: Spacing.xl },
  errorText: { color: 'rgba(255,255,255,0.62)', textAlign: 'center', lineHeight: 20 },
  retryBtn: { borderRadius: Radius.pill, backgroundColor: Colors.sunset, paddingHorizontal: 18, paddingVertical: 10 },
  retryText: { color: '#fff', fontWeight: Typography.extrabold, fontSize: Typography.sm },
});
